/**
 * arbi agent-create — create a bot/test account non-interactively
 *
 * Requires SUPPORT_API_KEY for CI verification flow.
 * Generates agent-{timestamp}@{deploymentDomain} accounts.
 *
 * Usage:
 *   SUPPORT_API_KEY=xxx arbi agent-create
 *   SUPPORT_API_KEY=xxx arbi agent-create https://my-server.com
 */

import { Command } from 'commander'
import { createArbiClient } from '@arbidocs/client'
import {
  getErrorMessage,
  performPasswordLogin,
  generateNewWorkspaceKey,
  workspaces as workspaceOps,
  projects as projectOps,
} from '@arbidocs/sdk'
import { success, error, ref, dim } from '../output.js'
import { store, updateConfig } from '../config.js'

const CENTRAL_API_URL = 'https://central.arbi.work'
const DEFAULT_PASSWORD = 'agent-dev-1234'

async function getVerificationCode(email: string, apiKey: string): Promise<string> {
  const params = new URLSearchParams({ email })
  const res = await fetch(`${CENTRAL_API_URL}/license-management/verify-ci?${params.toString()}`, {
    method: 'GET',
    headers: { 'x-api-key': apiKey },
  })

  if (!res.ok) {
    const body = await res.text().catch(() => '')
    throw new Error(`Failed to get verification code: ${res.status} ${body}`)
  }

  const data = (await res.json()) as { verification_words?: string[]; verification_code?: string }
  const words = data?.verification_words ?? data?.verification_code ?? null
  if (!words) throw new Error('No verification code in response')

  return Array.isArray(words) ? words.join(' ') : String(words)
}

export function registerAgentCreateCommand(program: Command): void {
  program
    .command('agent-create')
    .description(
      'Create a standalone bot/test ACCOUNT via SUPPORT_API_KEY (different from ' +
        '`arbi agent create`, which makes a child agent under the current user)'
    )
    .argument('[url]', 'Server URL (auto-detected if omitted)')
    .option('-p, --password <password>', 'Account password', DEFAULT_PASSWORD)
    .option('--workspace-name <name>', 'Workspace name', 'Agent Workspace')
    .option('--email <email>', 'Custom email (default: agent-{timestamp}@{domain})')
    .action(
      async (
        url: string | undefined,
        opts: { password: string; workspaceName: string; email?: string }
      ) => {
        // 1. Resolve server URL
        const { config, source } = store.resolveConfigWithFallbacks()
        if (url) {
          const domain = new URL(url).hostname
          config.baseUrl = url.replace(/\/$/, '')
          config.deploymentDomain = domain
          store.saveConfig(config)
          dim(`Server: ${config.baseUrl}`)
        } else {
          dim(`Server: ${config.baseUrl} (from ${source})`)
        }

        // 2. Check for SUPPORT_API_KEY
        const supportApiKey = process.env.SUPPORT_API_KEY
        if (!supportApiKey) {
          error(
            'SUPPORT_API_KEY is required.\n' +
              'Set it with: export SUPPORT_API_KEY=<key>\n' +
              'Or source from .env: source .env && arbi agent-create'
          )
          process.exit(1)
        }

        // 3. Resolve email
        const timestamp = Date.now()
        let email = opts.email || `agent-${timestamp}@${config.deploymentDomain}`
        if (!email.includes('@')) {
          email = `${email}@${config.deploymentDomain}`
        }
        dim(`Email: ${email}`)

        // 4. Register
        const arbiClient = createArbiClient({
          baseUrl: config.baseUrl,
          deploymentDomain: config.deploymentDomain,
          credentials: 'omit',
        })
        await arbiClient.crypto.initSodium()

        const verifyResponse = await arbiClient.fetch.POST('/v1/user/verify-email', {
          body: { email },
        })
        if (verifyResponse.error) {
          error(`verify-email failed: ${JSON.stringify(verifyResponse.error)}`)
          process.exit(1)
        }

        let verificationCode: string
        try {
          verificationCode = await getVerificationCode(email, supportApiKey)
        } catch (err) {
          error(`Failed to get verification code: ${getErrorMessage(err)}`)
          process.exit(1)
        }

        try {
          await arbiClient.auth.register({
            email,
            password: opts.password,
            verificationCode,
            firstName: 'Agent',
            lastName: `${timestamp}`,
          })
        } catch (err) {
          error(`Registration failed: ${getErrorMessage(err)}`)
          process.exit(1)
        }

        // 5. Login + create workspace
        try {
          const { arbi, loginResult } = await performPasswordLogin(
            config,
            email,
            opts.password,
            store
          )
          const userProjects = await projectOps.listProjects(arbi)
          const defaultProjectExtId = userProjects[0]?.external_id
          if (!defaultProjectExtId) throw new Error('No projects found for agent')
          const encryptedKey = await generateNewWorkspaceKey(arbi, loginResult.serverSessionKey)
          const ws = await workspaceOps.createWorkspace(
            arbi,
            opts.workspaceName,
            encryptedKey,
            defaultProjectExtId
          )
          updateConfig({ selectedWorkspaceId: ws.external_id })

          console.log('')
          success('Agent account created!')
          console.log('')
          console.log(`  Email:     ${ref(email)}`)
          console.log(`  Password:  ${ref(opts.password)}`)
          console.log(`  Workspace: ${ws.name} (${ref(ws.external_id)})`)
          console.log(`  Server:    ${ref(config.baseUrl)}`)
          console.log('')
          dim('Ready to use: arbi ask "hello"')
        } catch (err) {
          error(`Post-registration setup failed: ${getErrorMessage(err)}`)
          process.exit(1)
        }
      }
    )
}
